import React, { memo, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  MainTitle,
  PressableOpacity,
  SteppedProgressBar,
} from "app/components/common";
import {
  changeNavField,
  setRoute,
} from "@microdoing/shared/services/nav/actions";

import HeaderTabBar from "app/navigation/TabBar/HeaderTabBar";
import DailyWelcome from "./DailyWelcome";
import InformativeCard from "app/components/common/Cards/TimelineActivation/InformativeCard";
import ActionCard from "app/components/common/Cards/TimelineActivation/ActionCard";
import LogoFullMicrodoing from "app/components/Icons/logo/LogoFullMicrodoing";
import { Dimensions, ScrollView, StatusBar } from "react-native";
import theme from "app/Theme/Theme";
import { useDispatch, useSelector } from "react-redux";
import {
  getActiveStep,
  getCampaignActionGoal,
  getEventsWithActionGoals,
  getIsCampaignLockedForToday,
  getNextCategory,
  getTotalDoneSteps,
  getUserCampaignStrategy,
  getIsMbtiPopupDisplayed,
} from "@microdoing/shared/services/userCampaign/selectors";
import { getScrollTop } from "@microdoing/shared/services/nav/selectors";
import UserStats from "app/scenes/UserProfile/UserStats/UserStats";
import {
  getUser,
  getUserCurrentStreak,
} from "@microdoing/shared/services/user/selectors";
import { isWeb, isWebDesktop } from "@microdoing/shared/utils/deviceDetectors";
import TimelineCards from "./TimelineCards";
import i18n from "@microdoing/shared/i18n";
import { useNavigation } from "@react-navigation/native";
import {
  getStepCommentInfoByStepId,
  selectStep,
} from "@microdoing/shared/services/userCampaign/actions";
import { changeActionState } from "@microdoing/shared/services/action/actions";
import selectLanguage from "@microdoing/shared/utils/selectLanguage";
import { getLanguage } from "@microdoing/shared/services/profileSettings/selectors";
import removeHtmlTags from "@microdoing/shared/utils/removeHtmlTags";
import CancelActionBar from "./CancelActionBar";
import moment from "moment";
import { getTime } from "@microdoing/shared/services/general/selectors";
import normalize from "app/utils/normalize";
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withSpring,
  useDerivedValue,
  withTiming,
  withDelay,
  useAnimatedScrollHandler,
} from "react-native-reanimated";
import { TIME_LINE_CARD_HEIGHT } from "./TimelineCards";
import {
  getPrevRoute,
  getRoute,
} from "@microdoing/shared/services/nav/selectors";
import { useRoute } from "@react-navigation/native";
import { MbtiModal } from "app/components/common/MbtiModal";

const { height, width } = Dimensions.get("screen");

const CONTAINER_HEIGHT = height * 0.8;

const cardPadding = normalize(17);

const TOP_BAR_MARGIN = theme.spacing.s;
const TITLE_HEIGHT = normalize(60);
const TOP_BAR_HEIGHT = normalize(30);
const TITLE_MARGIN = theme.spacing.sm;
const STAT_BLOCK_HEIGHT = normalize(95);
const STAT_MARGIN = theme.spacing.sm;

const ACTION_CARD_HEIGHT = normalize(110);

const TIMELINE_TOP_MARGIN =
  TOP_BAR_MARGIN +
  TITLE_HEIGHT +
  TITLE_MARGIN +
  STAT_BLOCK_HEIGHT +
  STAT_MARGIN +
  TOP_BAR_HEIGHT; // ~200

const MAX_SCROLL = -TIMELINE_TOP_MARGIN + TOP_BAR_HEIGHT + TOP_BAR_MARGIN;

const ReanimatedBox = isWeb ? Box : Animated.createAnimatedComponent(Box);

const styles = {
  scrollView: {
    display: "flex",
    alignItems: isWebDesktop ? "flex-start" : "center",
    marginTop: !isWebDesktop && -5,
    columnGap: isWebDesktop ? theme.spacing.xm : 0,
    paddingTop: !isWebDesktop && theme.spacing.s,
    flexDirection: isWebDesktop ? "row" : "column",
    justifyContent: "center",
    width: "100%",
  },
};

interface IProps {}

const TimelineActivation = ({}: IProps) => {
  const [heightActionCard, setHeightActionCard] = useState(140);
  const [widthScreen, setWidthScreen] = useState(0);
  const [isMbtiModalVisible, setIsMbtiModalVisible] = useState(false);
  const scrollRef = useRef(null);

  const startY = useSharedValue(0);
  const scrollOffset = useSharedValue(0);
  const scrollViewPaddingTop = useSharedValue(0);
  const jouvieScroll = useSharedValue(0);
  const events = useSelector(getEventsWithActionGoals);
  const actionCardTranslateX = useSharedValue(0);

  const user = useSelector(getUser);
  const nextCategory = useSelector(getNextCategory);
  const activeStep = useSelector(getActiveStep);
  const language = useSelector(getLanguage);
  const time = useSelector(getTime);
  const campaignActionGoal = useSelector(getCampaignActionGoal);
  const totalDoneSteps = useSelector(getTotalDoneSteps);
  const shouldScrollTop = useSelector(getScrollTop);

  const isMbtiPopupDisplayed = useSelector(getIsMbtiPopupDisplayed);

  const onCloseMbtiModal = () => {
    setIsMbtiModalVisible(false);
  };

  const strategy = useSelector(getUserCampaignStrategy);

  const routeName = useSelector(getRoute);

  const isCampaignLockedForToday = useSelector(getIsCampaignLockedForToday);

  const streak = useSelector(getUserCurrentStreak);

  const { goals, hasGoals, skills, categories } = user;
  const numberOfCards = events?.length || 0;

  const animatedActionCard = useAnimatedStyle(() => {
    return {
      transform: [
        { translateX: actionCardTranslateX.value },
        {
          translateY:
            scrollOffset.value > MAX_SCROLL ? scrollOffset.value : MAX_SCROLL,
        },
      ],
    };
  });

  useEffect(() => {
    scrollToTop();
    dispatch(changeNavField({ scrollTop: false }));
  }, [shouldScrollTop]);

  useEffect(() => {
    dispatch(setRoute("Home"));
  }, []);

  useEffect(() => {
    if (routeName === "Home") {
      actionCardTranslateX.value = withSpring(0, {
        damping: 15,
        stiffness: 100,
      });
    } else if (routeName === "SelectAction") {
      actionCardTranslateX.value = withTiming(-width / 2);
    } else {
      actionCardTranslateX.value = withSpring(0, {
        damping: 15,
        stiffness: 100,
      });
    }
  }, [routeName]);

  const cardsMaxScroll = useMemo(() => {
    const totalCardsHeight =
      (TIME_LINE_CARD_HEIGHT + normalize(20)) * numberOfCards;
    if (totalCardsHeight > CONTAINER_HEIGHT)
      return totalCardsHeight + MAX_SCROLL;

    return -MAX_SCROLL * 2;
  }, [numberOfCards]);

  // Do not remove even if const is not used -> logic happens
  const derivedValue = useDerivedValue(() => {
    if (scrollOffset.value > MAX_SCROLL) {
      jouvieScroll.value = -scrollOffset.value;
    }
  }, [scrollOffset]);

  const currentStatus = useMemo(() => {
    if (activeStep?._id) return activeStep?.status;

    return "active";
  }, [activeStep]);

  const isSelected = useMemo(() => {
    return currentStatus === "selected";
  }, [currentStatus]);

  const scrollViewStyle = useAnimatedStyle(() => {
    return {
      transform: [
        {
          translateY:
            scrollOffset.value > MAX_SCROLL ? -scrollOffset.value : -MAX_SCROLL,
        },
      ],
    };
  }, []);

  const dispatch = useDispatch();
  const navigation = useNavigation();

  const { acceptDate } = activeStep;

  const handleCancelActionPress = () => {
    navigation.navigate("CancelAction");
  };

  const handleGoBackPress = () => {
    navigation.navigate("SelectAction");
  };

  const getHasBeenSelectedUnder10Minutes = timeSelectedSooner => {
    const momentTime = moment(timeSelectedSooner);

    const timeDiff = momentTime.diff(moment(acceptDate), "minute");

    return timeDiff < 10;
  };

  const getHasBeenSelectedToday = timeSelectedToday => {
    const momentTime = moment(timeSelectedToday);

    return momentTime.isSame(moment(acceptDate), "day");
  };

  const hasBeenSelectedToday = useMemo(() => {
    return getHasBeenSelectedToday(time);
  }, [acceptDate, time]);

  const hasActionBar = useMemo(() => {
    return (
      strategy !== "ordered" &&
      currentStatus === "selected" &&
      !hasBeenSelectedToday
    );
  }, [
    getHasBeenSelectedUnder10Minutes,
    getHasBeenSelectedToday,
    currentStatus,
    strategy,
  ]);

  const whiteBoxStyle = useAnimatedStyle(() => {
    return {
      top: 0,
      marginTop: TIMELINE_TOP_MARGIN + ACTION_CARD_HEIGHT / 2,
      transform: [
        {
          translateY:
            scrollOffset.value > MAX_SCROLL ? scrollOffset.value : MAX_SCROLL,
        },
      ],
    };
  }, [isSelected]);

  const backgroundStyle = useAnimatedStyle(() => {
    return {
      top: 0,
      marginTop: TIMELINE_TOP_MARGIN + TOP_BAR_MARGIN + ACTION_CARD_HEIGHT / 2,
      paddingTop: scrollViewPaddingTop.value,
      transform: [
        {
          translateY:
            scrollOffset.value > MAX_SCROLL ? scrollOffset.value : MAX_SCROLL,
        },
      ],
    };
  }, [isSelected]);

  useEffect(() => {
    if (isCampaignLockedForToday) {
      scrollViewPaddingTop.value = withTiming(0);
    } else if (currentStatus !== "selected") {
      scrollViewPaddingTop.value = withTiming(cardPadding);
    } else if (currentStatus === "selected") {
      scrollViewPaddingTop.value = withTiming(cardPadding);
    } else {
      scrollViewPaddingTop.value = withTiming(cardPadding);
    }
  }, [hasActionBar, currentStatus, isCampaignLockedForToday]);

  const getTotalSkillsPoints = useMemo(() => {
    const total = skills.reduce((prev, curr) => {
      return prev + curr.experience;
    }, 0);

    return total;
  }, [skills]);

  const findDimensionsActionCard = ({ height }) => {
    setHeightActionCard(height);
  };

  const handleOnLayoutActionCard = event => {
    findDimensionsActionCard(event.nativeEvent.layout);
  };

  const findDimensionsScreen = ({ width }) => {
    setWidthScreen(width);
  };

  const handleOnLayoutScreen = event => {
    findDimensionsScreen(event.nativeEvent.layout);
  };

  const handlePressActionCard = () => {
    if (isMbtiPopupDisplayed) {
      setIsMbtiModalVisible(true);
    } else if (currentStatus === "selected") {
      dispatch(
        selectStep({
          stepSelected: activeStep,
        }),
      );
      dispatch(getStepCommentInfoByStepId({ stepId: activeStep?.step }));
      dispatch(changeActionState({ seeActionAgain: true }));
      navigation.navigate("Action", { isComingFromAnalysis: false });
    } else if (isCampaignLockedForToday) {
      navigation.navigate("Team");
    } else {
      navigation.navigate("SelectAction");
    }
  };

  const scrollHandler = useAnimatedScrollHandler(event => {
    scrollOffset.value = -event.contentOffset.y;
  });

  const scrollToTop = () => {
    scrollRef?.current?.scrollTo({ x: 0, y: 0, animated: true });
  };

  const renderInformations = () => {
    return (
      <Box
        zIndex={2}
        paddingHorizontal={isWebDesktop ? "null" : "xm"}
        width="100%"
        marginTop={isWebDesktop ? "null" : "sm"}
      >
        <Box flexDirection="row" zIndex={2}>
          <Box flex={1} marginRight="sm" zIndex={2} height={90}>
            <InformativeCard
              stat={
                <MainTitle variant="l6-bold" style={{ marginTop: -5 }}>
                  {streak || 1}
                  <MainTitle variant="l4-regular"> x</MainTitle>
                </MainTitle>
              }
              description={i18n.t("current-streak")}
              progress={
                <SteppedProgressBar
                  totalDone={streak || 1}
                  numberOfSteps={streak || 1}
                />
              }
            />
          </Box>
          <Box flex={1} zIndex={2} height={90}>
            <InformativeCard
              stat={
                <MainTitle variant="l6-bold">
                  {`${totalDoneSteps}`}
                  <MainTitle variant="l4-medium">{` / ${campaignActionGoal}`}</MainTitle>
                </MainTitle>
              }
              description={i18n.t("timeline-action-done")}
              progress={
                <SteppedProgressBar
                  totalDone={totalDoneSteps}
                  numberOfSteps={campaignActionGoal}
                />
              }
            />
          </Box>
        </Box>
        {isWebDesktop && (
          <Box marginTop="sm">
            <UserStats
              skills={skills}
              goals={goals}
              hasGoals={hasGoals}
              categories={categories}
              totalSkillsPoints={getTotalSkillsPoints}
            />
          </Box>
        )}
      </Box>
    );
  };

  if (isWebDesktop) {
    return (
      <ScrollView
        showsVerticalScrollIndicator={false}
        contentContainerStyle={styles.scrollView}
        testID="progress-screen-scrollView"
      >
        <Box
          flexDirection="row"
          style={{ columnGap: theme.spacing.xm }}
          width="100%"
          onLayout={handleOnLayoutScreen}
        >
          <Box width={widthScreen / 2 - theme.spacing.xm / 2}>
            <Box
              backgroundColor="neutral.white"
              borderRadius={8}
              paddingVertical="xm"
              alignItems="center"
              width="100%"
            >
              <DailyWelcome scrollOffsetValue={jouvieScroll} />
              <Box
                width="100%"
                paddingHorizontal={{ phone: "xm", smallWeb: "l" }}
                onLayout={handleOnLayoutActionCard}
                zIndex={2}
                paddingTop="m"
              >
                <PressableOpacity onPress={handlePressActionCard}>
                  <ActionCard
                    selected={currentStatus === "selected"}
                    title={i18n.t("today")}
                    description={
                      currentStatus === "selected"
                        ? removeHtmlTags(
                            selectLanguage({
                              text: activeStep?.content?.title,
                              language,
                            }),
                          )
                        : i18n.t("new-challenges")
                    }
                    avatars={activeStep?.users}
                    category={
                      currentStatus === "selected"
                        ? activeStep.category
                        : nextCategory
                    }
                    gradientColor={
                      currentStatus === "selected"
                        ? [
                            theme.colors["gradient.greenToBlueGradient.start"],
                            theme.colors["gradient.greenToBlueGradient.end"],
                          ]
                        : [
                            theme.colors["gradient.blueGradient.start"],
                            theme.colors["gradient.blueGradient.end"],
                          ]
                    }
                  />
                </PressableOpacity>
                {strategy !== "ordered" && (
                  <>
                    {!getHasBeenSelectedToday(time) &&
                      currentStatus === "selected" && (
                        <CancelActionBar
                          onPress={handleCancelActionPress}
                          buttonText={i18n.t("change-today-action")}
                        />
                      )}
                    {getHasBeenSelectedUnder10Minutes(time) &&
                      currentStatus === "selected" &&
                      routeName !== "SelectAction" && (
                        <CancelActionBar
                          onPress={handleGoBackPress}
                          buttonText={i18n.t("back-to-selection", {
                            time:
                              10 -
                              moment(time).diff(moment(acceptDate), "minute"),
                          })}
                        />
                      )}
                  </>
                )}
              </Box>
              <TimelineCards reanimatedStyle={scrollViewStyle} />
            </Box>
          </Box>
          <Box
            width={widthScreen / 2 - theme.spacing.xm / 2}
            alignSelf="flex-start"
            borderColor="neutral.500"
            justifyContent="flex-start"
            alignItems="flex-start"
          >
            {renderInformations()}
          </Box>
        </Box>
      </ScrollView>
    );
  }

  return (
    <Box flex={1} position="relative">
      <MbtiModal
        isVisible={isMbtiPopupDisplayed && isMbtiModalVisible}
        onClose={onCloseMbtiModal}
      />
      <HeaderTabBar
        paddingHorizontal="xm"
        title={
          <PressableOpacity onPress={scrollToTop}>
            <LogoFullMicrodoing />
          </PressableOpacity>
        }
      />

      <Box position="absolute" width="100%" top={50} zIndex={2}>
        <DailyWelcome scrollOffsetValue={jouvieScroll} />
        {renderInformations()}
      </Box>
      <ReanimatedBox
        style={whiteBoxStyle}
        position="absolute"
        zIndex={2}
        borderRadius={10}
        paddingHorizontal="xm"
        alignItems="center"
        backgroundColor="neutral.white"
        width="100%"
        height={CONTAINER_HEIGHT * 2}
      />

      <Animated.ScrollView
        ref={scrollRef}
        scrollsToTop
        style={{
          position: "relative",
          width,
          zIndex: 3,
        }}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={{
          zIndex: 3,
        }}
        onScroll={scrollHandler}
        scrollEventThrottle={16}
      >
        <ReanimatedBox
          style={[backgroundStyle]}
          zIndex={3}
          paddingHorizontal="xm"
          width="100%"
          minHeight={height - TIMELINE_TOP_MARGIN}
        >
          <TimelineCards reanimatedStyle={scrollViewStyle} />
        </ReanimatedBox>
      </Animated.ScrollView>
      <ReanimatedBox
        style={animatedActionCard}
        top={TIMELINE_TOP_MARGIN}
        position="absolute"
        width="100%"
        paddingHorizontal="xm"
        zIndex={25}
      >
        <PressableOpacity
          style={{ zIndex: 20 }}
          onPress={handlePressActionCard}
        >
          <ActionCard
            canChangeAction={
              !getHasBeenSelectedToday(time) && currentStatus === "selected"
            }
            hasChangeAction={
              getHasBeenSelectedUnder10Minutes(time) &&
              currentStatus === "selected" &&
              routeName !== "SelectAction"
            }
            isLockedForToday={isCampaignLockedForToday}
            isSelected={currentStatus === "selected"}
            title={
              isCampaignLockedForToday
                ? i18n.t("challenge-done-today")
                : i18n.t("today")
            }
            description={
              isCampaignLockedForToday
                ? i18n.t("pulse-your-team")
                : currentStatus === "selected"
                ? removeHtmlTags(
                    selectLanguage({
                      text: activeStep.title,
                      language,
                    }),
                  )
                : i18n.t("new-challenges")
            }
            avatars={activeStep.users}
            category={
              currentStatus === "selected" ? activeStep.category : nextCategory
            }
            gradientColor={
              currentStatus === "selected"
                ? [
                    theme.colors["gradient.greenToBlueGradient.start"],
                    theme.colors["gradient.greenToBlueGradient.end"],
                  ]
                : [
                    theme.colors["gradient.blueGradient.start"],
                    theme.colors["gradient.blueGradient.end"],
                  ]
            }
          />
        </PressableOpacity>
      </ReanimatedBox>
    </Box>
  );
};

export default memo(TimelineActivation);
